import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Plane, Plus, Search, Clock, MapPin, Users as UsersIcon,
    Loader2, PlaneTakeoff, UserCheck, ArrowRight, MessageSquare,
    Send, ChevronDown, Pencil, Trash2, List, CalendarDays, GanttChart, History
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { UnconfirmedCrewCard } from '../components/crew/UnconfirmedCrewCard';
import { CrewPositionSelect } from '../components/crew/CrewPositionSelect';
import { TrainingFlightControl } from '../components/crew/TrainingFlightControl';
import { posLabel, FICHA_LABEL } from '../components/crew/crewPositions';
import { AuditHistoryModal } from '../components/AuditHistoryModal';
import { FlightCalendarView } from '../components/flights/FlightCalendarView';
import { FlightTimelineView } from '../components/flights/FlightTimelineView';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
    scheduled: { label: 'Programado', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', next: ['confirmed', 'cancelled'] },
    confirmed: { label: 'Confirmado', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', next: ['preparing', 'cancelled'] },
    preparing: { label: 'Preparando', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', next: ['boarding', 'delayed', 'cancelled'] },
    boarding: { label: 'Abordando', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', next: ['closed', 'delayed'] },
    closed: { label: 'Cerrado', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', next: ['departed'] },
    departed: { label: 'Despegado', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', next: ['en_route'] },
    en_route: { label: 'En Vuelo', color: 'bg-green-500/10 text-green-400 border-green-500/20', next: ['arrived'] },
    arrived: { label: 'Llegado', color: 'bg-emerald-600/10 text-emerald-500 border-emerald-600/20', next: [] },
    delayed: { label: 'Demorado', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', next: ['boarding', 'cancelled'] },
    cancelled: { label: 'Cancelado', color: 'bg-red-500/10 text-red-400 border-red-500/20', next: [] },
};

const MSG_AREAS = [
    { value: 'all', label: 'Todo el equipo' }, { value: 'operations', label: 'Operaciones' }, { value: 'dispatch', label: 'Despacho' },
    { value: 'counter', label: 'Mostrador' }, { value: 'ramp', label: 'Rampa' }, { value: 'maintenance', label: 'Mantenimiento' },
    { value: 'crew', label: 'Tripulación' }, { value: 'cco', label: 'CCO' },
];


const statusRoleHint = {
    confirmed: 'CCO / Despacho de Vuelo',
    preparing: 'CCO / Despacho de Vuelo',
    boarding: 'CCO / Mostrador',
    closed: 'CCO / Mostrador',
    departed: 'CCO / Despacho de Vuelo',
    en_route: 'CCO / Despacho de Vuelo',
    arrived: 'CCO / Despacho de Vuelo',
    delayed: 'CCO / Despacho de Vuelo',
    cancelled: 'CCO',
};

// Motivo obligatorio para forzar (admin). Avisa si se cancela o es muy corto.
const askForceReason = (msg) => {
    const r = window.prompt(`${msg}\n\nComo administrador puedes asignar de todas formas. Escribe el motivo (mínimo 10 caracteres); quedará en la auditoría:`);
    if (r === null) { toast.info('Asignación no forzada'); return null; }
    if (r.trim().length < 10) { toast.error('Motivo requerido (mínimo 10 caracteres). No se forzó la asignación.'); return null; }
    return r;
};

export default function FlightsPage() {
    const { token, hasRole, user } = useAuth();
    const headers = { Authorization: `Bearer ${token}` };
    const [flights, setFlights] = useState([]);
    const [aircraft, setAircraft] = useState([]);
    const [crew, setCrew] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');

    // Dialogs
    const [showAssignAircraft, setShowAssignAircraft] = useState(null);
    const [showAssignCrew, setShowAssignCrew] = useState(null);
    const [showDetail, setShowDetail] = useState(null);
    const [showMessage, setShowMessage] = useState(null);
    const [selectedAircraft, setSelectedAircraft] = useState('');
    const [selectedCrew, setSelectedCrew] = useState([]);
    // Auditoria 4 - Item 17: rol por tripulante ('operating' | 'deadhead_op')
    const [crewRoles, setCrewRoles] = useState({});
    const [crewPositions, setCrewPositions] = useState({});
    const [crewTraining, setCrewTraining] = useState({});
    const [posInfo, setPosInfo] = useState(null);
    // Auditoria 6 - Item 7: filtros para asignar tripulación
    const [crewFilterPos, setCrewFilterPos] = useState('');
    const [crewFilterRatingOnly, setCrewFilterRatingOnly] = useState(false);
    const [messages, setMessages] = useState([]);
    const [msgContent, setMsgContent] = useState('');
    const [msgPriority, setMsgPriority] = useState('normal');
    const [msgArea, setMsgArea] = useState('all');
    const [submitting, setSubmitting] = useState(false);
    const [editFlight, setEditFlight] = useState(null);
    const [auditFlight, setAuditFlight] = useState(null);
    const [editData, setEditData] = useState({});
    const [viewMode, setViewMode] = useState('list');

    // FASE 5: Vuelo Especial (charter)
    const [showSpecial, setShowSpecial] = useState(false);
    const [specialData, setSpecialData] = useState({
        flight_number: '', origin: '', destination: '',
        scheduled_departure: '', scheduled_arrival: '', is_charter: true, notes: ''
    });

    useEffect(() => { fetchData(); }, []);
    // Auditoria 4 - Item 19: auto-refresh cada 30s cuando no hay dialogos abiertos
    useAutoRefresh(() => fetchData(), 30000, !showAssignCrew && !showAssignAircraft && !showDetail && !editFlight && !showSpecial && !showMessage);

    const handleCreateSpecial = async () => {
        // FASE 5: crear vuelo suelto/charter (backend ya soporta POST /flights)
        if (!specialData.flight_number || !specialData.origin || !specialData.destination || !specialData.scheduled_departure) {
            toast.error('Completa: numero, origen, destino y salida programada');
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/flights`, {
                ...specialData,
                origin: specialData.origin.toUpperCase(),
                destination: specialData.destination.toUpperCase(),
            }, { headers });
            toast.success('Vuelo especial creado');
            setShowSpecial(false);
            setSpecialData({ flight_number: '', origin: '', destination: '', scheduled_departure: '', scheduled_arrival: '', is_charter: true, notes: '' });
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear vuelo especial');
        } finally {
            setSubmitting(false);
        }
    };

    const fetchData = async () => {
        try {
            const [fRes, aRes, cRes] = await Promise.all([
                axios.get(`${API}/flights`, { headers }),
                axios.get(`${API}/aircraft`, { headers }),
                axios.get(`${API}/crew`, { headers }).catch(() => ({ data: [] }))
            ]);
            setFlights(fRes.data);
            setAircraft(aRes.data);
            setCrew(cRes.data);
        } catch { toast.error('Error al cargar vuelos'); }
        finally { setLoading(false); }
    };

    // Status change
    const handleStatusChange = async (flightId, newStatus) => {
        try {
            await axios.patch(`${API}/flights/${flightId}/status?status=${newStatus}`, null, { headers });
            toast.success(`Estado: ${statusConfig[newStatus]?.label}`);
            fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No tienes permiso para cambiar a este estado');
        }
    };

    // Assign aircraft (A1: AOG/traslape bloquean; habilitación pide justificación CCO/admin; capacidad pide confirmación)
    const handleAssignAircraft = async () => {
        if (!showAssignAircraft || !selectedAircraft) return;
        setSubmitting(true);
        const errText = (d) => typeof d === 'string' ? d : d?.message || 'Error al asignar aeronave';
        const extra = {};
        try {
            for (let attempt = 0; attempt < 4; attempt++) {
                try {
                    const ar = await axios.patch(`${API}/flights/${showAssignAircraft.id}/assign-aircraft`, { aircraft_id: selectedAircraft, ...extra }, { headers });
                    toast.success(extra.rating_override_reason ? 'Cambio de equipo autorizado con justificación' : 'Aeronave asignada');
                    if (ar.data?.info) toast.info(ar.data.info, { duration: 10000 });
                    setShowAssignAircraft(null);
                    setSelectedAircraft('');
                    fetchData();
                    return;
                } catch (e) {
                    const detail = e.response?.data?.detail;
                    if (e.response?.status !== 409) { toast.error(errText(detail), { duration: 10000 }); return; }
                    if (detail?.type === 'crew_rating_mismatch') {
                        const list = (detail.crew || []).map(c => `• ${c.name} (${c.position}) — habilitaciones: ${(c.ratings || []).join(', ') || 'ninguna'}`).join('\n');
                        if (!detail.can_override) {
                            toast.error(`${detail.message} Cambia la tripulación o pide al Centro de Control (CCO) que autorice el cambio.`, { duration: 14000 });
                            return;
                        }
                        const why = window.prompt(`Tripulación sin habilitación para ${detail.aircraft_type}:\n${list}\n\nCambia la tripulación, o escribe la justificación para autorizar (mínimo 20 caracteres). Quedará en la auditoría y se avisará al despachador:`);
                        if (why === null) { toast.info('Cambio de equipo cancelado'); return; }
                        if (why.trim().length < 20) { toast.error('La justificación debe tener al menos 20 caracteres. No se aplicó el cambio.'); return; }
                        extra.rating_override_reason = why.trim();
                    } else if (detail?.type === 'capacity_warning') {
                        if (!window.confirm(detail.message)) { toast.info('Cambio de equipo cancelado'); return; }
                        extra.confirm_impacts = true;
                    } else if (detail?.conflicts) {
                        const list = (detail.conflicts || []).map(c => c.flight_number || c.conflicting_flight_number);
                        const msg = `${detail.message}${list.length ? `: ${list.slice(0, 3).join(', ')}${list.length > 3 ? ` y ${list.length - 3} más` : ''}` : ''}`;
                        if (user?.role !== 'admin') { toast.error(`${msg}. Solo un administrador puede autorizar la excepción.`, { duration: 10000 }); return; }
                        const forceReason = askForceReason(msg);
                        if (!forceReason) return;
                        extra.force = true;
                        extra.reason = forceReason.trim();
                    } else {
                        toast.error(errText(detail), { duration: 10000 });
                        return;
                    }
                }
            }
        } finally { setSubmitting(false); }
    };

    // Assign crew
    const handleAssignCrew = async () => {
        if (!showAssignCrew) return;
        setSubmitting(true);
        // Auditoria 4 - Item 17: enviar assignments con rol por tripulante
        const assignments = selectedCrew.map(cid => {
            const op = (crewRoles[cid] || 'operating') === 'operating';
            const tr = crewTraining[cid];
            return { crew_id: cid, role: crewRoles[cid] || 'operating', flight_position: op ? (crewPositions[cid] || null) : null,
                ...(op && tr !== undefined ? { in_training: tr !== false, training_reason: tr === false ? null : tr } : {}) };
        });
        try {
            const res = await axios.patch(`${API}/flights/${showAssignCrew.id}/assign-crew`, { assignments }, { headers });
            toast.success('Tripulación asignada');
            if (res.data?.info) toast.info(res.data.info, { duration: 10000 });
            if (res.data?.fdtl_warnings?.length > 0) {
                res.data.fdtl_warnings.forEach(w => toast.warning(w.message, { duration: 8000 }));
            }
            setShowAssignCrew(null);
            setSelectedCrew([]);
            setCrewRoles({});
            fetchData();
        } catch (e) {
            const detail = e.response?.data?.detail;
            let forceReason;
            // Auditoria 5 - Item 4: bloqueo duro de type_ratings (sin bypass con force)
            if (e.response?.status === 409 && detail?.type === 'missing_type_rating') {
                toast.error(detail.message, { duration: 12000 });
            } else if (e.response?.status === 409 && detail?.conflicts) {
                const list = (detail.conflicts || []).map(c => c.flight_number || c.conflicting_flight_number);
                const msg = `${detail.message}${list.length ? `: ${list.slice(0, 3).join(', ')}${list.length > 3 ? ` y ${list.length - 3} más` : ''}` : ''}`;
                if (user?.role !== 'admin') {
                    toast.error(`${msg}. Solo un administrador puede autorizar la excepción.`, { duration: 10000 });
                } else if ((forceReason = askForceReason(msg))) {
                    try {
                        const res = await axios.patch(`${API}/flights/${showAssignCrew.id}/assign-crew`, { assignments, force: true, reason: forceReason.trim() }, { headers });
                        toast.success('Tripulación asignada (con conflicto)');
                        if (res.data?.info) toast.info(res.data.info, { duration: 10000 });
                        if (res.data?.fdtl_warnings?.length > 0) {
                            res.data.fdtl_warnings.forEach(w => toast.warning(w.message, { duration: 8000 }));
                        }
                        setShowAssignCrew(null);
                        setSelectedCrew([]);
                        setCrewRoles({});
                        fetchData();
                    } catch (e2) {
                        const d2 = e2.response?.data?.detail;
                        // El force NO evita el bloqueo por type_rating (safety)
                        if (e2.response?.status === 409 && d2?.type === 'missing_type_rating') {
                            toast.error(d2.message, { duration: 12000 });
                        } else {
                            toast.error(typeof d2 === 'string' ? d2 : d2?.message || 'Error al asignar tripulacion');
                        }
                    }
                }
            } else if (e.response?.status === 409 && detail?.type === 'fdtl_exceeded') {
                const msg = (detail.violations || []).map(v => v.message).join('\n') || detail.message;
                if (user?.role !== 'admin') {
                    toast.error(`${msg}. Solo un administrador puede autorizar la excepción con justificación.`, { duration: 12000 });
                    return;
                }
                const why = window.prompt(`Límite FDTL excedido:\n${msg}\n\nPara asignar de todas formas escribe la justificación (mínimo 20 caracteres). Quedará registrada en la auditoría:`);
                if (why === null) { toast.info('Asignación cancelada'); return; }
                if (why.trim().length < 20) { toast.error('La justificación debe tener al menos 20 caracteres. No se asignó.'); return; }
                try {
                    const fr = await axios.patch(`${API}/flights/${showAssignCrew.id}/assign-crew`, { assignments, force: true, reason: why.trim(), fdtl_override_reason: why.trim() }, { headers });
                    toast.success('Tripulación asignada con excepción FDTL justificada');
                    if (fr.data?.info) toast.info(fr.data.info, { duration: 10000 });
                    setShowAssignCrew(null);
                    setSelectedCrew([]);
                    setCrewRoles({});
                    fetchData();
                } catch (e2) {
                    const d2 = e2.response?.data?.detail;
                    toast.error(typeof d2 === 'string' ? d2 : d2?.message || 'Error al asignar tripulacion', { duration: 12000 });
                }
            } else {
                toast.error(typeof detail === 'string' ? detail : detail?.message || 'Error al asignar tripulacion');
            }
        } finally { setSubmitting(false); }
    };

    const toggleCrewMember = (id) => {
        setSelectedCrew(prev => {
            if (prev.includes(id)) return prev.filter(c => c !== id);
            // al agregar, garantiza rol por defecto
            setCrewRoles(rr => (rr[id] ? rr : { ...rr, [id]: 'operating' }));
            return [...prev, id];
        });
    };

    const setCrewRole = (id, role) => {
        setCrewRoles(prev => ({ ...prev, [id]: role }));
    };

    // Messages (A4: usa el módulo de Mensajes Operacionales, que notifica a los destinatarios)
    const openMessages = async (flight) => {
        setShowMessage(flight);
        try {
            const res = await axios.get(`${API}/op-messages?flight_id=${flight.id}`, { headers });
            setMessages(res.data);
        } catch { setMessages([]); }
    };

    const sendMessage = async () => {
        if (!msgContent.trim() || !showMessage) return;
        try {
            await axios.post(`${API}/op-messages`, {
                message_type: 'general',
                flight_id: showMessage.id,
                flight_number: showMessage.flight_number,
                aircraft_registration: showMessage.aircraft_registration || '',
                title: `Mensaje operacional · ${showMessage.flight_number}`,
                body: msgContent,
                priority: msgPriority,
                affected_areas: msgArea === 'all' ? [] : [msgArea],
            }, { headers });
            setMsgContent('');
            const res = await axios.get(`${API}/op-messages?flight_id=${showMessage.id}`, { headers });
            setMessages(res.data);
            toast.success('Mensaje enviado y notificado a los destinatarios');
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Error al enviar mensaje');
        }
    };

    // Edit flight
    const openEditFlight = (f) => {
        setEditData({
            flight_number: f.flight_number || '', origin: f.origin || '', destination: f.destination || '',
            scheduled_departure: f.scheduled_departure?.slice(0,16) || '', scheduled_arrival: f.scheduled_arrival?.slice(0,16) || ''
        });
        setEditFlight(f);
    };
    const saveEditFlight = async () => {
        if (!editFlight) return;
        try {
            await axios.put(`${API}/flights/${editFlight.id}`, editData, { headers });
            toast.success('Vuelo actualizado');
            setEditFlight(null);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar vuelo'); }
    };

    // Delete flight
    const deleteFlight = async (f) => {
        if (!window.confirm(`Eliminar vuelo ${f.flight_number}? Se eliminaran pasajeros, despacho y servicios asociados.`)) return;
        try {
            await axios.delete(`${API}/flights/${f.id}`, { headers });
            toast.success(`Vuelo ${f.flight_number} eliminado`);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar vuelo'); }
    };

    // Filters
    const isPilot = hasRole('pilot') && !hasRole('admin') && !hasRole('ccod');
    const pilotCrewId = isPilot ? crew.find(c => c.email === user?.email)?.id : null;

    const filtered = flights.filter(f => {
        // Pilot only sees their assigned flights
        if (isPilot && pilotCrewId) {
            const isAssigned = f.assigned_crew?.some(ac => ac.crew_id === pilotCrewId);
            if (!isAssigned) return false;
        }
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            if (!f.flight_number?.toLowerCase().includes(s) && !f.origin?.toLowerCase().includes(s) && !f.destination?.toLowerCase().includes(s)) return false;
        }
        if (statusFilter !== 'all' && f.status !== statusFilter) return false;
        if (dateFilter && !f.scheduled_departure?.startsWith(dateFilter)) return false;
        return true;
    });

    const canAssignAircraft = hasRole('admin') || hasRole('ccod') || hasRole('dispatcher');
    const canAssignCrew = hasRole('admin') || hasRole('crew_admin') || hasRole('dispatcher') || hasRole('ccod');
    const canChangeStatus = hasRole('admin') || hasRole('ccod') || hasRole('dispatcher') || hasRole('counter');

    return (
        <div className="space-y-6">
            <UnconfirmedCrewCard key={JSON.stringify((flights || []).map(f => (f.assigned_crew || []).map(c => c.confirmed)))} onChanged={fetchData} />
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="flights-title">
                        Vuelos
                    </h1>
                    <p className="text-muted-foreground mt-1">Gestion operacional de vuelos</p>
                </div>
                <div className="flex items-center gap-2">
                    {hasRole(['admin', 'dispatcher', 'ccod', 'comercial']) && (
                        <Button size="sm" variant="outline" onClick={() => setShowSpecial(true)} data-testid="flights-special-btn" className="gap-1.5">
                            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Vuelo Especial</span>
                        </Button>
                    )}
                    <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1" data-testid="view-mode-toggle">
                        {[
                            { mode: 'list', icon: List, label: 'Lista' },
                            { mode: 'calendar', icon: CalendarDays, label: 'Calendario' },
                            { mode: 'timeline', icon: GanttChart, label: 'Timeline' },
                        ].map(({ mode, icon: Icon, label }) => (
                            <Button
                                key={mode}
                                variant={viewMode === mode ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode(mode)}
                                className={`gap-1.5 text-xs ${viewMode === mode ? '' : 'text-muted-foreground'}`}
                                data-testid={`view-${mode}-btn`}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{label}</span>
                            </Button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Loading spinner */}
            {loading && (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
            )}

            {/* Calendar View */}
            {viewMode === 'calendar' && !loading && (
                <FlightCalendarView flights={flights} onFlightClick={(f) => openEditFlight(f)} />
            )}

            {/* Timeline View */}
            {viewMode === 'timeline' && !loading && (
                <FlightTimelineView flights={flights} onFlightClick={(f) => openEditFlight(f)} />
            )}

            {/* Filters - only in list mode */}
            {viewMode === 'list' && (
            <>
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar vuelo, origen, destino..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 input-aviation" data-testid="flights-search" />
                </div>
                <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-[160px] input-aviation font-mono" data-testid="flights-date-filter" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="flights-status-filter">
                        <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {Object.entries(statusConfig).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Flights List */}
            {!loading && (
                <div className="space-y-3" data-testid="flights-list">
                    {filtered.map((flight) => {
                        const st = statusConfig[flight.status] || statusConfig.scheduled;
                        const nextStatuses = st.next || [];
                        const hasAircraft = !!flight.aircraft_id || !!flight.aircraft_registration;
                        const hasCrew = flight.assigned_crew?.length > 0;

                        return (
                            <Card key={flight.id} className="card-aviation" data-testid={`flight-${flight.id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between flex-wrap gap-3">
                                        {/* Flight info */}
                                        <div className="flex items-center gap-5">
                                            <div className="flex items-center gap-3 min-w-[120px]">
                                                <Plane className="w-5 h-5 text-primary flex-shrink-0" />
                                                <div>
                                                    <p className="font-bold font-mono text-lg leading-none" data-testid={`flight-number-${flight.id}`}>{flight.flight_number}</p>
                                                    <p className="text-xs text-muted-foreground mt-0.5">{flight.scheduled_departure?.split('T')[0]}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                                                <span className="font-mono font-bold">{flight.origin}</span>
                                                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                                                <span className="font-mono font-bold">{flight.destination}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="w-3.5 h-3.5" />
                                                <span className="font-mono">
                                                    {flight.scheduled_departure?.split('T')[1]?.slice(0,5) || '--:--'}
                                                    {' - '}
                                                    {flight.scheduled_arrival?.split('T')[1]?.slice(0,5) || '--:--'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Assignment indicators + actions */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {/* Aircraft badge/button */}
                                            {canAssignAircraft ? (
                                                <button
                                                    onClick={() => { setShowAssignAircraft(flight); setSelectedAircraft(flight.aircraft_id || ''); }}
                                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer border ${hasAircraft ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20' : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'}`}
                                                    data-testid={`assign-ac-${flight.id}`}
                                                >
                                                    <PlaneTakeoff className="w-3.5 h-3.5" />
                                                    {flight.aircraft_registration || 'Asignar A/C'}
                                                </button>
                                            ) : (
                                                <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${hasAircraft ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                                                    <PlaneTakeoff className="w-3.5 h-3.5" />{flight.aircraft_registration || 'Sin A/C'}
                                                </span>
                                            )}

                                            {/* Crew badge/button */}
                                            {canAssignCrew ? (
                                                <button
                                                    onClick={() => {
                                                        setShowAssignCrew(flight);
                                                        const ids = flight.assigned_crew?.map(c => c.crew_id) || [];
                                                        setSelectedCrew(ids);
                                                        const roles = {};
                                                        const posMap = {};
                                                        const trMap = {};
                                                        (flight.assigned_crew || []).forEach(c => { roles[c.crew_id] = c.role || 'operating'; if (c.flight_position) posMap[c.crew_id] = c.flight_position; if (c.in_training) trMap[c.crew_id] = c.training_reason || ''; });
                                                        setCrewRoles(roles);
                                                        setCrewPositions(posMap);
                                                        setCrewTraining(trMap);
                                                        setPosInfo(null);
                                                        axios.get(`${API}/flights/${flight.id}/crew-positions`, { headers }).then(r => setPosInfo(r.data)).catch(() => setPosInfo({ training_flight: { enabled: false }, cabin_slots: ['TCP1', 'TCP2'] }));
                                                    }}
                                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer border ${hasCrew ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20' : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'}`}
                                                    data-testid={`assign-crew-${flight.id}`}
                                                >
                                                    <UserCheck className="w-3.5 h-3.5" />
                                                    {hasCrew ? `${flight.assigned_crew.length} crew` : 'Asignar Crew'}
                                                </button>
                                            ) : (
                                                <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${hasCrew ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                                                    <UsersIcon className="w-3.5 h-3.5" />{hasCrew ? `${flight.assigned_crew.length} crew` : 'Sin crew'}
                                                </span>
                                            )}
                                            {flight.assigned_crew?.some(c => c.confirmed === false) && (
                                                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/40" data-testid={`crew-unconfirmed-${flight.id}`}>Tripulación sin confirmar</span>
                                            )}

                                            {/* Auditoria 6 - Item 22: PAX with load factor real, freeze on arrived */}
                                            {(() => {
                                                const pax = flight.pax_total || flight.passengers || 0;
                                                const reserved = flight.pax_reserved || 0;
                                                const cap = flight.aircraft_capacity || 0;
                                                const arrived = flight.status === 'arrived';
                                                const finalCount = flight.final_pax_count;
                                                const showFinal = arrived && finalCount != null;
                                                const displayCount = showFinal ? finalCount : reserved;
                                                const loadPct = flight.occupancy_pct != null ? flight.occupancy_pct : (cap > 0 ? Math.round((displayCount / cap) * 100) : 0);
                                                const loadColor = cap === 0 ? 'text-muted-foreground' : loadPct >= 80 ? 'text-emerald-500' : loadPct >= 50 ? 'text-amber-500' : 'text-muted-foreground';
                                                return (
                                                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cap > 0 && loadPct >= 80 ? 'bg-emerald-500/10' : cap > 0 && loadPct >= 50 ? 'bg-amber-500/10' : ''}`} data-testid={`pax-load-${flight.id}`}>
                                                        <UsersIcon className={`w-3 h-3 ${loadColor}`} />
                                                        <span className={loadColor}>
                                                            {showFinal ? (
                                                                <><b>{finalCount}</b>{cap > 0 ? `/${cap}` : ''} finales</>
                                                            ) : reserved > 0 ? (
                                                                <>{reserved}{cap > 0 ? `/${cap}` : ''} res{pax > 0 ? ` | ${pax} chk` : ''}</>
                                                            ) : (
                                                                <>{pax}{cap > 0 ? `/${cap}` : ''} PAX</>
                                                            )}
                                                            {cap > 0 && <span className="ml-1 font-mono">({loadPct}%)</span>}
                                                        </span>
                                                    </span>
                                                );
                                            })()}

                                            {/* Messages */}
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openMessages(flight)} data-testid={`msg-${flight.id}`}>
                                                <MessageSquare className="w-3.5 h-3.5" />
                                            </Button>

                                            {/* Edit/Delete */}
                                            {(hasRole('admin') || hasRole('ccod')) && flight.status !== 'departed' && flight.status !== 'arrived' && (
                                                <>
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditFlight(flight)} data-testid={`edit-flight-${flight.id}`}>
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteFlight(flight)} data-testid={`delete-flight-${flight.id}`}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </>
                                            )}
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground" onClick={() => setAuditFlight(flight)} data-testid={`audit-flight-${flight.id}`} title="Historial de cambios">
                                                <History className="w-3.5 h-3.5" />
                                            </Button>

                                            {/* Status badge with dropdown (Auditoria 3 - Item 1: DropdownMenu por clic) */}
                                            {canChangeStatus && nextStatuses.length > 0 ? (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${st.color} cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40`}
                                                            data-testid={`status-btn-${flight.id}`}
                                                        >
                                                            {st.label} <ChevronDown className="w-3 h-3" />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="min-w-[200px]">
                                                        {nextStatuses.map(ns => (
                                                            <DropdownMenuItem
                                                                key={ns}
                                                                onClick={() => handleStatusChange(flight.id, ns)}
                                                                data-testid={`status-${ns}-${flight.id}`}
                                                                className="flex items-center justify-between gap-3"
                                                            >
                                                                <span>{statusConfig[ns]?.label}</span>
                                                                <span className="text-[10px] text-muted-foreground">{statusRoleHint[ns]}</span>
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            ) : (
                                                <Badge className={st.color} variant="outline" data-testid={`status-${flight.id}`}>{st.label}</Badge>
                                            )}
                                        </div>
                                    </div>

                                    {/* Crew names row */}
                                    {hasCrew && (
                                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                                            {flight.training_flight?.enabled && <span className="text-xs px-2 py-0.5 rounded bg-sky-500/10 text-sky-700 dark:text-sky-400" data-testid={`training-badge-${flight.id}`}>Vuelo de instrucción</span>}
                                            {flight.assigned_crew.map((c, i) => (
                                                <span key={i} className="text-xs px-2 py-0.5 rounded bg-muted/50 text-muted-foreground" data-testid={`crew-chip-${flight.id}-${c.crew_id}`}>
                                                    {c.name} <span className="text-[10px] opacity-60">({c.role === 'deadhead_op' ? 'DH' : posLabel(c.flight_position || { captain: 'PIC', first_officer: 'FO', flight_attendant: 'TCP' }[c.position] || c.position)}{c.in_training ? ', instrucción' : ''})</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                    {filtered.length === 0 && (
                        <Card className="card-aviation">
                            <CardContent className="p-12 text-center text-muted-foreground">
                                <Plane className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>No se encontraron vuelos</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
            </>
            )}

            {/* Assign Aircraft Dialog */}
            <Dialog open={!!showAssignAircraft} onOpenChange={() => setShowAssignAircraft(null)}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <PlaneTakeoff className="w-5 h-5 text-primary" />
                            Asignar Aeronave — {showAssignAircraft?.flight_number}
                        </DialogTitle>
                        <DialogDescription>Selecciona la aeronave que operara este vuelo.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {showAssignAircraft?.origin} → {showAssignAircraft?.destination} | {showAssignAircraft?.scheduled_departure?.split('T')[0]}
                        </p>
                        <div className="space-y-2">
                            <Label>Aeronave</Label>
                            <Select value={selectedAircraft} onValueChange={setSelectedAircraft}>
                                <SelectTrigger data-testid="select-aircraft"><SelectValue placeholder="Seleccionar aeronave" /></SelectTrigger>
                                <SelectContent>
                                    {aircraft.map(a => {
                                        const st = (a.operational_status || 'active');
                                        const blocked = st === 'aog' || st === 'maintenance';
                                        return (
                                            <SelectItem key={a.id} value={a.id} disabled={blocked || st === 'storage'} data-testid={`aircraft-option-${a.registration}`}>
                                                {a.registration} — {a.aircraft_type}{blocked ? ` · ${st === 'aog' ? 'AOG' : 'En mantenimiento'} (no disponible)` : st === 'storage' ? ' · Almacenada' : ''}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAssignAircraft(null)}>Cancelar</Button>
                        <Button onClick={handleAssignAircraft} disabled={submitting || !selectedAircraft} className="btn-primary" data-testid="submit-assign-aircraft">
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Asignar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Crew Dialog */}
            <Dialog open={!!showAssignCrew} onOpenChange={() => setShowAssignCrew(null)}>
                <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <UserCheck className="w-5 h-5 text-primary" />
                            Asignar Tripulación — {showAssignCrew?.flight_number}
                        </DialogTitle>
                        <DialogDescription>Selecciona los tripulantes que operaran este vuelo.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                            {showAssignCrew?.origin} → {showAssignCrew?.destination} | {showAssignCrew?.scheduled_departure?.split('T')[0]}
                        </p>
                        {posInfo && <TrainingFlightControl flightId={showAssignCrew?.id} training={posInfo.training_flight} canEdit={hasRole(['admin', 'crew_admin', 'ccod'])}
                            onChanged={tf => { setPosInfo(p => ({ ...p, training_flight: tf })); fetchData(); }} />}
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Selecciona tripulantes</p>
                        {/* Auditoria 6 - Item 7: filtros por posición y habilitación de tipo */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <select value={crewFilterPos} onChange={e => setCrewFilterPos(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="crew-filter-position">
                                <option value="">Todas las posiciones</option>
                                <option value="captain">Capitán</option>
                                <option value="first_officer">Primer Oficial</option>
                                <option value="flight_attendant">TCP</option>
                            </select>
                            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                                <input type="checkbox" checked={crewFilterRatingOnly} onChange={e => setCrewFilterRatingOnly(e.target.checked)} data-testid="crew-filter-rating" />
                                Solo con habilitación para <span className="font-mono">{showAssignCrew?.aircraft_type || showAssignCrew?.aircraft_registration || 'esta A/C'}</span>
                            </label>
                        </div>
                        <div className="space-y-1.5 max-h-[380px] overflow-y-auto">
                            {crew.filter(c => {
                                if (crewFilterPos && c.position !== crewFilterPos) return false;
                                if (crewFilterRatingOnly) {
                                    const acType = showAssignCrew?.aircraft_type || '';
                                    if (!acType) return true;
                                    const norm = s => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                                    const acNorm = norm(acType);
                                    const now = new Date().toISOString();
                                    const ratings = c.type_ratings || [];
                                    const has = ratings.some(r => {
                                        const rn = norm(r.aircraft_type);
                                        if (!rn || !(rn.includes(acNorm) || acNorm.includes(rn))) return false;
                                        return !r.expires_at || r.expires_at > now;
                                    });
                                    if (!has) return false;
                                }
                                return true;
                            }).map(c => {
                                const isSelected = selectedCrew.includes(c.id);
                                const role = crewRoles[c.id] || 'operating';
                                return (
                                    <div
                                        key={c.id}
                                        className={`rounded-lg border transition-colors ${isSelected ? 'bg-primary/10 border-primary/30' : 'bg-muted/20 border-border/30 hover:bg-muted/40'}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleCrewMember(c.id)}
                                            className="w-full flex items-center justify-between p-3 text-left text-sm"
                                            data-testid={`crew-select-${c.id}`}
                                        >
                                            <div>
                                                <p className="font-medium">{c.name || `${c.first_name} ${c.last_name}`}</p>
                                                <p className="text-xs text-muted-foreground">{FICHA_LABEL[c.position] || c.position} {c.license_number ? `— ${c.license_number}` : ''}</p>
                                            </div>
                                            {isSelected && <Badge className="bg-primary/20 text-primary text-xs">Seleccionado</Badge>}
                                        </button>
                                        {isSelected && (
                                            <div className="px-3 pb-3 -mt-1 flex items-center gap-2">
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Rol</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setCrewRole(c.id, 'operating')}
                                                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${role === 'operating' ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' : 'bg-muted/40 text-muted-foreground border-border/30 hover:bg-muted/60'}`}
                                                    data-testid={`crew-role-op-${c.id}`}
                                                >
                                                    Operando
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCrewRole(c.id, 'deadhead_op')}
                                                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${role === 'deadhead_op' ? 'bg-amber-500/15 text-amber-500 border-amber-500/30' : 'bg-muted/40 text-muted-foreground border-border/30 hover:bg-muted/60'}`}
                                                    data-testid={`crew-role-dh-${c.id}`}
                                                >
                                                    DH operacional
                                                </button>
                                                {role === 'operating' && posInfo && (
                                                    <div className="ml-auto">
                                                        <CrewPositionSelect member={c} value={crewPositions[c.id]} training={!!posInfo.training_flight?.enabled} cabinSlots={posInfo.cabin_slots}
                                                            onChange={v => setCrewPositions(prev => ({ ...prev, [c.id]: v }))}
                                                            trainingReason={crewTraining[c.id]} onTrainingChange={v => setCrewTraining(prev => ({ ...prev, [c.id]: v }))} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {crew.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No hay tripulantes registrados</p>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {selectedCrew.length} tripulantes seleccionados —
                            {' '}Operando: {selectedCrew.filter(id => (crewRoles[id] || 'operating') === 'operating').length}
                            {' '}· DH: {selectedCrew.filter(id => crewRoles[id] === 'deadhead_op').length}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAssignCrew(null)}>Cancelar</Button>
                        <Button onClick={handleAssignCrew} disabled={submitting} className="btn-primary" data-testid="submit-assign-crew">
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Asignar Tripulación
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Messages Dialog */}
            <Dialog open={!!showMessage} onOpenChange={() => setShowMessage(null)}>
                <DialogContent className="sm:max-w-[550px] max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-primary" />
                            Mensajes — {showMessage?.flight_number}
                        </DialogTitle>
                        <DialogDescription>Historial de mensajes operacionales del vuelo.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Message list */}
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {messages.map(m => {
                                const priorityColors = { urgent: 'border-l-red-500', high: 'border-l-amber-500', normal: 'border-l-border', low: 'border-l-muted' };
                                return (
                                    <div key={m.id} className={`p-3 rounded-lg bg-muted/20 border-l-2 ${priorityColors[m.priority] || 'border-l-border'}`} data-testid={`msg-item-${m.id}`}>
                                        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                            <span className="font-medium text-foreground">{m.sender_name} <span className="text-muted-foreground">({m.sender_role})</span></span>
                                            {m.priority !== 'normal' && <Badge variant="outline" className="text-[10px]">{m.priority}</Badge>}
                                        </div>
                                        <p className="text-sm">{m.body || m.content}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{new Date(m.created_at).toLocaleString('es-ES')}</p>
                                    </div>
                                );
                            })}
                            {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sin mensajes operacionales</p>}
                        </div>
                        {/* Send message */}
                        <div className="border-t border-border/50 pt-3 space-y-2">
                            <div className="flex gap-2">
                                <Select value={msgPriority} onValueChange={setMsgPriority}>
                                    <SelectTrigger className="w-[110px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Baja</SelectItem>
                                        <SelectItem value="normal">Normal</SelectItem>
                                        <SelectItem value="high">Alta</SelectItem>
                                        <SelectItem value="urgent">Urgente</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={msgArea} onValueChange={setMsgArea}>
                                    <SelectTrigger className="w-[140px]" data-testid="msg-area"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {MSG_AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Input
                                    value={msgContent}
                                    onChange={(e) => setMsgContent(e.target.value)}
                                    placeholder="Escribir mensaje operacional..."
                                    className="flex-1 input-aviation"
                                    data-testid="msg-input"
                                    onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                                />
                                <Button onClick={sendMessage} size="sm" className="btn-primary" data-testid="msg-send">
                                    <Send className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Flight Dialog */}
            <Dialog open={!!editFlight} onOpenChange={() => setEditFlight(null)}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-tight flex items-center gap-2">
                            <Pencil className="w-5 h-5 text-primary" />Editar Vuelo — {editFlight?.flight_number}
                        </DialogTitle>
                        <DialogDescription>Modifica los datos operacionales del vuelo.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5"><Label>Numero de Vuelo</Label><Input value={editData.flight_number || ''} onChange={e => setEditData({...editData, flight_number: e.target.value.toUpperCase()})} data-testid="edit-flight-number" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Origen</Label><Input value={editData.origin || ''} onChange={e => setEditData({...editData, origin: e.target.value.toUpperCase()})} maxLength={4} data-testid="edit-flight-origin" /></div>
                            <div className="space-y-1.5"><Label>Destino</Label><Input value={editData.destination || ''} onChange={e => setEditData({...editData, destination: e.target.value.toUpperCase()})} maxLength={4} data-testid="edit-flight-dest" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5"><Label>Salida</Label><Input type="datetime-local" value={editData.scheduled_departure || ''} onChange={e => setEditData({...editData, scheduled_departure: e.target.value})} data-testid="edit-flight-dep" /></div>
                            <div className="space-y-1.5"><Label>Llegada</Label><Input type="datetime-local" value={editData.scheduled_arrival || ''} onChange={e => setEditData({...editData, scheduled_arrival: e.target.value})} data-testid="edit-flight-arr" /></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditFlight(null)}>Cancelar</Button>
                        <Button onClick={saveEditFlight} className="btn-primary" data-testid="save-edit-flight">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* FASE 5: Vuelo Especial / Charter */}
            <Dialog open={showSpecial} onOpenChange={setShowSpecial}>
                <DialogContent className="sm:max-w-lg" data-testid="special-flight-dialog">
                    <DialogHeader>
                        <DialogTitle>Nuevo Vuelo Especial / Charter</DialogTitle>
                        <DialogDescription>
                            Crea un vuelo suelto para charter, ferry, ambulancia o vuelos privados fuera del programa regular.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Numero de vuelo</Label>
                            <Input value={specialData.flight_number} onChange={e => setSpecialData({ ...specialData, flight_number: e.target.value })} placeholder="Ej: CHT-9001" data-testid="special-flight-number" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Origen (IATA)</Label>
                                <Input value={specialData.origin} onChange={e => setSpecialData({ ...specialData, origin: e.target.value })} maxLength={4} data-testid="special-origin" />
                            </div>
                            <div>
                                <Label>Destino (IATA)</Label>
                                <Input value={specialData.destination} onChange={e => setSpecialData({ ...specialData, destination: e.target.value })} maxLength={4} data-testid="special-destination" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Salida programada</Label>
                                <Input type="datetime-local" value={specialData.scheduled_departure} onChange={e => setSpecialData({ ...specialData, scheduled_departure: e.target.value })} data-testid="special-dep" />
                            </div>
                            <div>
                                <Label>Llegada programada</Label>
                                <Input type="datetime-local" value={specialData.scheduled_arrival} onChange={e => setSpecialData({ ...specialData, scheduled_arrival: e.target.value })} data-testid="special-arr" />
                            </div>
                        </div>
                        <div>
                            <Label>Notas</Label>
                            <Input value={specialData.notes} onChange={e => setSpecialData({ ...specialData, notes: e.target.value })} placeholder="Charter privado, ambulancia, ferry..." />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSpecial(false)}>Cancelar</Button>
                        <Button onClick={handleCreateSpecial} className="btn-primary" disabled={submitting} data-testid="special-create-btn">
                            {submitting ? 'Creando...' : 'Crear Vuelo'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* FASE 2 UI: Historial de auditoria del vuelo */}
            <AuditHistoryModal
                open={!!auditFlight}
                onOpenChange={(o) => { if (!o) setAuditFlight(null); }}
                collection="flights"
                entityId={auditFlight?.id}
                entityLabel={auditFlight ? `${auditFlight.flight_number} · ${auditFlight.origin}→${auditFlight.destination}` : ''}
            />
        </div>
    );
}
